import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AssertivaContato {
  id: string;
  consulta_id: string | null;
  cnpj: string | null;
  cpf: string | null;
  tipo: string;
  valor: string;
  classificacao: string | null;
  score_confianca: number | null;
  dados_extra: any;
  created_at: string;
}

export interface AssertivaSocio {
  id: string;
  cnpj: string | null;
  cpf_cnpj_socio: string | null;
  nome: string | null;
  qualificacao: string | null;
  participacao: number | null;
  data_entrada: string | null;
  decisor: boolean;
  dados_extra: any;
  created_at: string;
}

function dedup<T extends { id: string }>(arr: T[]): T[] {
  const seen = new Set<string>();
  return arr.filter(item => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export function useAssertivaContatos(
  cnpj?: string | null,
  cpf?: string | null,
  consultaIds?: string[] | null,
  email?: string | null,
  telefone?: string | null,
  nome?: string | null,
  leadId?: string | null
) {
  const key = cnpj || cpf;
  const field = cnpj ? 'cnpj' : 'cpf';

  // Build all possible keys for historical lookup
  const allKeys = [
    cnpj?.replace(/\D/g, ''),
    cpf?.replace(/\D/g, ''),
    email?.trim(),
    telefone?.replace(/\D/g, ''),
    nome?.trim(),
  ].filter(Boolean) as string[];

  // 1. Buscar contatos pelo CNPJ/CPF direto
  const contatosQuery = useQuery({
    queryKey: ['assertiva-contatos', field, key],
    queryFn: async () => {
      if (!key) return [];
      const digits = key.replace(/\D/g, '');
      const { data, error } = await supabase
        .from('assertiva_contatos')
        .select('*')
        .eq(field, digits)
        .order('score_confianca', { ascending: false });
      if (error) throw error;
      return (data || []) as AssertivaContato[];
    },
    enabled: !!key,
  });

  // 2. Buscar todas as consulta_ids históricas vinculadas a QUALQUER identificador do lead OU ao lead_id
  const historicalConsultasQuery = useQuery({
    queryKey: ['assertiva-historical-consultas', leadId, ...allKeys],
    queryFn: async () => {
      const ids: string[] = [];

      // Buscar por lead_id (recupera consultas manuais como CPF de sócio)
      if (leadId) {
        const { data: byLead, error: e1 } = await supabase
          .from('assertiva_consultas')
          .select('id')
          .eq('lead_id', leadId);
        if (!e1 && byLead) ids.push(...byLead.map(d => d.id));
      }

      // Buscar por allKeys (método original)
      if (allKeys.length > 0) {
        const { data: byKeys, error: e2 } = await supabase
          .from('assertiva_consultas')
          .select('id')
          .in('chave_consulta', allKeys);
        if (!e2 && byKeys) ids.push(...byKeys.map(d => d.id));
      }

      // Dedup
      return [...new Set(ids)];
    },
    enabled: !!leadId || allKeys.length > 0,
  });

  // Combinar consulta_ids do histórico com os passados pelo componente
  const allConsultaIds = dedup(
    [
      ...(historicalConsultasQuery.data || []).map(id => ({ id })),
      ...(consultaIds || []).map(id => ({ id })),
    ]
  ).map(x => x.id);

  // 3. Buscar contatos por todos os consulta_ids
  const contatosByConsultaQuery = useQuery({
    queryKey: ['assertiva-contatos-consulta', allConsultaIds],
    queryFn: async () => {
      if (allConsultaIds.length === 0) return [];
      const { data, error } = await supabase
        .from('assertiva_contatos')
        .select('*')
        .in('consulta_id', allConsultaIds)
        .order('score_confianca', { ascending: false });
      if (error) throw error;
      return (data || []) as AssertivaContato[];
    },
    enabled: allConsultaIds.length > 0,
  });

  // 4. Buscar sócios pelo CNPJ direto
  const sociosQuery = useQuery({
    queryKey: ['assertiva-socios', key],
    queryFn: async () => {
      if (!key) return [];
      const digits = key.replace(/\D/g, '');
      const { data, error } = await supabase
        .from('assertiva_socios')
        .select('*')
        .eq('cnpj', digits)
        .order('decisor', { ascending: false });
      if (error) throw error;
      return (data || []) as AssertivaSocio[];
    },
    enabled: !!cnpj,
  });

  // 5. Buscar sócios por todos os consulta_ids
  const sociosByConsultaQuery = useQuery({
    queryKey: ['assertiva-socios-consulta', allConsultaIds],
    queryFn: async () => {
      if (allConsultaIds.length === 0) return [];
      const { data, error } = await supabase
        .from('assertiva_socios')
        .select('*')
        .in('consulta_id', allConsultaIds)
        .order('decisor', { ascending: false });
      if (error) throw error;
      return (data || []) as AssertivaSocio[];
    },
    enabled: allConsultaIds.length > 0,
  });

  const refetch = () => {
    contatosQuery.refetch();
    sociosQuery.refetch();
    contatosByConsultaQuery.refetch();
    sociosByConsultaQuery.refetch();
    historicalConsultasQuery.refetch();
  };

  // Merge + deduplicate
  const contatos = dedup([
    ...(contatosByConsultaQuery.data || []),
    ...(contatosQuery.data || []),
  ]);
  const allSocios = dedup([
    ...(sociosByConsultaQuery.data || []),
    ...(sociosQuery.data || []),
  ]);

  const telefones = contatos.filter(c => c.tipo === 'celular' || c.tipo === 'telefone_fixo');
  const emails = contatos.filter(c => c.tipo === 'email');
  const enderecos = contatos.filter(c => c.tipo === 'endereco');
  const todosDadosCadastrais = contatos.filter(c => c.tipo === 'dados_cadastrais');
  const dadosCadastrais = todosDadosCadastrais[0] || null;
  const cnaesSecundarias = contatos.find(c => c.tipo === 'cnaes_secundarias') || null;
  const redesSociais = contatos.filter(c => c.tipo === 'rede_social');
  const participacoes = contatos.filter(c => c.tipo === 'participacao_empresa');

  return {
    contatos,
    telefones,
    emails,
    enderecos,
    dadosCadastrais,
    todosDadosCadastrais,
    cnaesSecundarias,
    redesSociais,
    participacoes,
    socios: allSocios,
    isLoading: contatosQuery.isLoading || sociosQuery.isLoading || contatosByConsultaQuery.isLoading || sociosByConsultaQuery.isLoading,
    ultimaConsulta: contatos[0]?.created_at || null,
    refetch,
  };
}
